// @ts-check
import React from "react";
import { useIntl } from "react-intl";
import { Box, VStack } from "@chakra-ui/react";

import useGetTransactionsWithFilters from "api/invoicing/useGetTransactionsWithFilters";
import EmptyState from "components/EmptyState";

import {
  TransactionList,
  TransactionListSkeletons,
  EmptyStateTransactions,
} from "../components/TransactionList";
import TransactionFiltersSection from "../components/TransactionFiltersSection";

import { INITIAL_PENDING_SELECTED_STATUSES, reduceSelectedFilters } from "../utils";

/**
 * PendingTab
 * @description
 * Fetches and displays a list of pending transactions for the transaction history flow
 * @param {object} props
 * @param {string} props.accountId account ID of the user to retrieve transaction history data.
 * @param {object} props.filters Object with filters for query params
 * @param {Array<SelectableTransactionFilter>} props.filters.types Array of UI state of Transaction type filters
 * @param {function} props.toggleTxnType toggle UI state of given transaction type
 * @param {boolean} props.expandFilters state for expanding filter buttons
 * @param {function} props.onClickFilterButton click handler for filter button
 * @returns {JSX.Element}
 */
export default function PendingTab({
  accountId,
  filters: { types },
  toggleTxnType,
  expandFilters,
  onClickFilterButton,
}) {
  const { formatMessage } = useIntl();
  const { data, isPending: isLoading, isError, error } = useGetTransactionsWithFilters(accountId, {
    types: reduceSelectedFilters(types),
    statuses: INITIAL_PENDING_SELECTED_STATUSES,
  });

  const renderEmptyState = () => {
    if (isLoading) {
      return <TransactionListSkeletons />;
    }

    if (isError) {
      return <EmptyState error={error} />;
    }

    if (!data?.payload?.transactions || data.payload.transactions.length <= 0) {
      return (
        <EmptyStateTransactions
          title={formatMessage({
            defaultMessage: "0 Transactions",
            description: "Zero state for history results",
          })}
          subtitle={formatMessage({
            defaultMessage: "You have 0 pending transactions",
            description: "Message to user",
          })}
        />
      );
    }

    return null;
  };

  return (
    <VStack align="stretch">
      <TransactionFiltersSection
        show={expandFilters}
        onClickFilterButton={onClickFilterButton}
        filters={types}
        onSelect={toggleTxnType}
      />
      <Box padding="17px">
        {renderEmptyState() ?? (
          <TransactionList sectionTitle="Pending" transactions={data.payload.transactions} />
        )}
      </Box>
    </VStack>
  );
}
